import cloneDeep from 'lodash-es/cloneDeep';
import { mergeAndRemoveDuplicates, policyDetailsFromSegments } from '@ourbranch/policy-utils';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

// Reconstruct the full details of the segment identified by `segmentId` by merging
// segments[0..that segment].
//
// Farm-based policies (F/Z/Y) persist later segments as *deltas* (only the changed
// fields) and represent a removed risk/location/item with an `isDeleted` marker rather
// than an absent entry. The generic reduce below merges via lodash `assignWith`
// (`mergeAndRemoveDuplicates`), which shallow-REPLACES the `farmLocations` array with the
// delta's partial array — dropping untouched risks and leaving a removed risk as a
// field-stripped stub (no `building`/`coverages`/`characteristicsData`). The farm renderer
// then reads those missing fields and crashes (GitHub #32515). Route farm policies through
// the delta-aware merge (`policyDetailsFromSegments` → farm-utils `applyFarmObjectDelta`),
// which removes `isDeleted` entries and preserves the base fields. `ignoreRenewalSegments`
// is disabled so this matches the generic path: merge exactly up to `segmentId`, nothing
// filtered by date.
// A farm segment the CFM GO import wrote carries `deltaDirectives` (#37380), which GraphQL returns as an
// AWSJSON string and as null on every other segment. The reader needs the object; a null must not ride
// into the merged details, which are sent back as PolicyDetailsInput (no such field).
const withDeltaDirectives = (segments) =>
  segments.map((segment) => {
    if (!segment || !('deltaDirectives' in segment)) {
      return segment;
    }
    const { deltaDirectives, ...rest } = segment;
    if (deltaDirectives == null) {
      return rest;
    }
    return {
      ...rest,
      deltaDirectives: typeof deltaDirectives === 'string' ? JSON.parse(deltaDirectives) : deltaDirectives
    };
  });

export const getMerged = (segments, segmentId, policyType) => {
  if (isFarmBasedPolicyType(policyType)) {
    return policyDetailsFromSegments(
      { policyType, segments: withDeltaDirectives(segments) },
      { upToSegmentId: segmentId, ignoreRenewalSegments: false, keepPremium: true }
    );
  }
  const index = segments.findIndex((item) => item.segmentId === segmentId);
  const initialValue = segments[0];
  return segments.slice(0, index + 1).reduce(mergeAndRemoveDuplicates, cloneDeep(initialValue));
};

export const getMergedAllSegments = (segments) => segments.reduce(mergeAndRemoveDuplicates, cloneDeep(segments[0]));
